import React, { useEffect, useState } from 'react';
import { userService } from '../services/api';
import Icon from '../components/Icon';

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('students');
  const [teacherForm, setTeacherForm] = useState({ name: '', email: '', password: '' });
  const [studentForm, setStudentForm] = useState({ name: '', email: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null); // { text, link }
  const [loading, setLoading] = useState(false);
  const [emailReady, setEmailReady] = useState(true);

  useEffect(() => {
    userService.getAll().then(setUsers).catch(() => {});
    userService.emailStatus().then((s) => setEmailReady(s.configured)).catch(() => {});
  }, []);

  const switchTab = (t) => {
    setTab(t);
    setError('');
    setNotice(null);
  };

  const showInviteResult = (result, name) => {
    if (result.emailSent) setNotice({ text: `Login email sent to ${name}. They will set their own password from the link.` });
    else setNotice({ text: result.note, link: result.link });
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setError('');
    setNotice(null);
    setLoading(true);
    try {
      const result = await userService.addStudent(studentForm);
      setUsers((prev) => [result.user, ...prev]);
      showInviteResult(result, result.user.name);
      setStudentForm({ name: '', email: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (u) => {
    setError('');
    setNotice(null);
    try {
      showInviteResult(await userService.resendInvite(u._id), u.name);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const created = await userService.addTeacher(teacherForm);
      setUsers((prev) => [created, ...prev]);
      setTeacherForm({ name: '', email: '', password: '' });
      setNotice({ text: `Teacher ${created.name} added. Share the email and password with them.` });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try {
      await userService.delete(id);
      setUsers((prev) => prev.filter((u) => u._id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).then(() => alert('Link copied'));

  const teachers = users.filter((u) => u.role === 'teacher');
  const students = users.filter((u) => u.role === 'student');
  const list = tab === 'teachers' ? teachers : students;

  return (
    <div className="admin-page">
      <div className="dashboard-header">
        <div>
          <h2>Admin Panel <span className="role-badge">Admin</span></h2>
          <p>{teachers.length} teachers &nbsp;·&nbsp; {students.length} students</p>
        </div>
        <div className="backup-box">
          <a className="btn-cta-primary backup-btn" href={userService.backupUrl()}>
            <Icon name="file" size={18} /> Download Backup
          </a>
          <small>All data and uploaded files in one ZIP. Download weekly and keep it somewhere safe.</small>
        </div>
      </div>

      <div className="tab-bar">
        <button className={`tab-btn ${tab === 'students' ? 'active' : ''}`} onClick={() => switchTab('students')}>
          Students ({students.length})
        </button>
        <button className={`tab-btn ${tab === 'teachers' ? 'active' : ''}`} onClick={() => switchTab('teachers')}>
          Teachers ({teachers.length})
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}
      {notice && (
        <div className="admin-notice">
          <p>{notice.text}</p>
          {notice.link && (
            <div className="admin-notice__link">
              <code>{notice.link}</code>
              <button type="button" className="approve-btn" onClick={() => copy(notice.link)}>Copy link</button>
            </div>
          )}
        </div>
      )}

      {tab === 'students' && (
        <div className="admin-subjects__add">
          <h3 className="dash-section-title">Add Student</h3>
          <p className="admin-help">
            The student gets an email with a link to set their own password (valid 7 days).
            {!emailReady && ' Email is not set up yet: you will get the link to share yourself.'}
          </p>
          <form className="admin-subjects__form" onSubmit={handleAddStudent}>
            <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
              <label>Name</label>
              <input value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} required />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
              <label>Email</label>
              <input type="email" value={studentForm.email} onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })} required />
            </div>
            <button type="submit" className="approve-btn" style={{ alignSelf: 'flex-end', padding: '0.65rem 1.25rem' }} disabled={loading}>
              {loading ? 'Sending...' : '+ Add Student'}
            </button>
          </form>
        </div>
      )}

      {tab === 'teachers' && (
        <div className="admin-subjects__add">
          <h3 className="dash-section-title">Add Teacher</h3>
          <form className="admin-subjects__form" onSubmit={handleAddTeacher}>
            <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
              <label>Name</label>
              <input value={teacherForm.name} onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })} required />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
              <label>Email</label>
              <input type="email" value={teacherForm.email} onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })} required />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
              <label>Password (8+ characters)</label>
              <input type="password" minLength={8} value={teacherForm.password} onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })} required />
            </div>
            <button type="submit" className="approve-btn" style={{ alignSelf: 'flex-end', padding: '0.65rem 1.25rem' }} disabled={loading}>
              {loading ? 'Adding...' : '+ Add Teacher'}
            </button>
          </form>
        </div>
      )}

      <div className="table-wrap">
        <table className="bookings-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Status</th><th>Joined</th><th>Action</th></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u._id}>
                <td style={{ fontWeight: 600 }}>{u.name}</td>
                <td>{u.email}</td>
                <td>
                  {u.passwordSet === false
                    ? <span className="status-badge status-pending">Invited</span>
                    : <span className="status-badge status-accepted">Active</span>}
                </td>
                <td style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td className="admin-actions">
                  {u.role === 'student' && u.passwordSet === false && (
                    <button className="action-btn action-accept" onClick={() => handleResend(u)}>Resend invite</button>
                  )}
                  <button className="action-btn action-reject" onClick={() => handleDelete(u._id)}>Delete</button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '2rem' }}>No {tab} yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
